import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Box,
  Typography,
  Alert,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import { z } from 'zod';
import React from 'react';
import { toast } from 'sonner';
import { captureException } from '@sentry/react';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormProvider, useForm, SubmitErrorHandler } from 'react-hook-form';

import type {
  CustomStep,
  CustomStepType,
  PatientIntakeStep,
  IntakeStatusBaseSteps,
} from '@shared/patient-intake-status';

import { useAuth, useUserDataContext } from '../../../../hooks/index.js';
import { getStepStatus } from '../utils';
import { getStepConfig } from '../getStepConfig';
import { usePatientIntakeStatusContext } from '../hooks';
import { NoteFormSection } from './NoteFormSection.tsx';
import { StepApprovalSection } from './StepApprovalSection';
import { updateIntakeStatus } from './updateIntakeStatus.ts';
import { StatusSelectorFormSection } from './StatusSelectorFormSection.tsx';
import { AccountVerificationResolutionForm } from './AccountVerificationResolutionForm.tsx';

const DevSection = ({ step }: { step: PatientIntakeStep }) => {
  if (!import.meta.env.DEV || !import.meta.env.VITE_DEBUG) {
    return null;
  }

  return (
    <Accordion sx={{ backgroundColor: 'divider' }}>
      <AccordionSummary sx={{ mt: 2 }}>
        <Typography>CompleteStepDialog ...step</Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ overflow: 'scroll' }}>
        <pre>{JSON.stringify(step, null, 2)}</pre>
      </AccordionDetails>
    </Accordion>
  );
};

const validationSchema = z
  .object({
    status: z.enum(['COMPLETED', 'PENDING', 'NOT_STARTED', 'ERROR'] as const),
    notes: z.string(),
    isNoteVisibleToPatient: z.boolean(),
    isMedicalNote: z.boolean(),
    keepCurrentNote: z.boolean(),
  })
  .superRefine((val, ctx) => {
    // If keeping current note, notes should be empty
    if (val.keepCurrentNote && val.notes.trim() !== '') {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Cannot add new notes when keeping current note',
        path: ['notes'],
      });
    }

    // If not keeping current note and notes are provided, they should not be empty
    if (!val.keepCurrentNote && val.notes.trim() !== '' && val.notes.trim().length < 1) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Notes cannot be empty',
        path: ['notes'],
      });
    }

    // isNoteVisibleToPatient should only be true if notes are provided and not keeping current note
    if (val.isNoteVisibleToPatient && (val.keepCurrentNote || val.notes.trim() === '')) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Cannot make note visible to patient without adding new notes',
        path: ['isNoteVisibleToPatient'],
      });
    }

    // isMedicalNote should only be true if notes are provided and not keeping current note
    if (val.isMedicalNote && (val.keepCurrentNote || val.notes.trim() === '')) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'Cannot make medical note without adding new notes',
        path: ['isMedicalNote'],
      });
    }
  });

export type CompleteStepFormData = z.infer<typeof validationSchema>;

type BaseStepTitleProps = {
  step: IntakeStatusBaseSteps[keyof IntakeStatusBaseSteps];
};

const BaseStepTitle = ({ step }: BaseStepTitleProps) => {
  const { isHealthcareProvider } = usePatientIntakeStatusContext();

  const stepConfig = getStepConfig(step.id);

  const title = stepConfig.title({
    step,
    isHealthcareProvider,
  });

  return (
    <DialogTitle component="div">
      <Typography variant="h6">{title}</Typography>
    </DialogTitle>
  );
};

type CustomStepTitleProps = {
  step: CustomStep[CustomStepType];
};

const CustomStepTitle = ({ step }: CustomStepTitleProps) => (
  <DialogTitle component="div">
    <Typography variant="h6">{step.title}</Typography>
    <Typography variant="body2">{step.description}</Typography>
  </DialogTitle>
);

type TitleProps = {
  step: React.ComponentProps<typeof StepApprovalSection>['step'];
};

const Title = ({ step }: TitleProps) => {
  if (step.stepType === 'BASE_STEP') {
    return <BaseStepTitle step={step} />;
  }

  return <CustomStepTitle step={step} />;
};

type CompleteStepDialogProps = {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
} & React.ComponentProps<typeof StepApprovalSection>;

export const CompleteStepDialog: React.FC<CompleteStepDialogProps> = ({ open, onClose, onSuccess, step }) => {
  const { user, userType } = useAuth();
  const { userData } = useUserDataContext();
  const { patientId, isHealthcareProvider } = usePatientIntakeStatusContext();

  const currentNote = step.currentStatus?.note ?? null;

  const methods = useForm<CompleteStepFormData>({
    defaultValues: {
      status: getStepStatus(step),
      notes: '',
      isNoteVisibleToPatient: false,
      isMedicalNote: false,
      keepCurrentNote: false,
    },
    resolver: zodResolver(validationSchema),
  });

  const {
    control,
    formState: { errors, isValid, isDirty, isSubmitting },
    handleSubmit,
    reset,
    setError,
  } = methods;

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleOnSubmit = async (data: CompleteStepFormData) => {
    try {
      await updateIntakeStatus({
        user,
        userData,
        userType,
        patientId,
        step: step,
        status: data.status,
        ...(data.keepCurrentNote
          ? { note: currentNote }
          : {
              notes: data.notes.trim() || null,
              isNoteVisibleToPatient: data.isNoteVisibleToPatient,
              isMedicalNote: data.isMedicalNote,
            }),
      });

      toast.success('Status updated successfully');
      onSuccess();
      handleClose();
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error({ error, data });
      }

      captureException(error, {
        tags: { issueIn: 'CompleteStepDialog.handleOnSubmit' },
        extra: { data },
      });

      setError('root', {
        type: 'manual',
        message: 'Failed to update status. Please try again.',
      });

      toast.error('Failed to update status');
    }
  };

  const handleOnSubmitError: SubmitErrorHandler<CompleteStepFormData> = (errors) => {
    if (import.meta.env.DEV) {
      console.error('Form validation errors:', errors);
    }

    toast.error('Please fix the form errors and try again');
  };

  const isUnresolvedAccountVerification =
    step.stepType === 'BASE_STEP' &&
    step.id === 'ACCOUNT_VERIFICATION' &&
    step.currentStatus.status !== 'COMPLETED';

  if (!open || !isHealthcareProvider) {
    return null;
  }

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <Title step={step} />
      <DialogContent>
        <Box sx={{ mb: 2 }}>
          <StepApprovalSection step={step} />
        </Box>

        {isUnresolvedAccountVerification ? (
          <AccountVerificationResolutionForm step={step} onSuccess={onSuccess} onClose={handleClose} />
        ) : (
          <FormProvider {...methods}>
            {errors.root && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {errors.root.message}
              </Alert>
            )}

            <Box sx={{ mb: 2 }}>
              <StatusSelectorFormSection control={control} step={step} />
            </Box>

            <Box sx={{ mb: 2 }}>
              <NoteFormSection currentNote={currentNote} stepId={step.id ?? null} />
            </Box>

            <DevSection step={step} />

            <DialogActions>
              <Button onClick={handleClose} disabled={isSubmitting}>
                Cancel
              </Button>
              <Button
                onClick={handleSubmit(handleOnSubmit, handleOnSubmitError)}
                variant="contained"
                color="primary"
                disabled={!isValid || isSubmitting || !isDirty}
                loading={isSubmitting}
              >
                Update Status
              </Button>
            </DialogActions>
          </FormProvider>
        )}
      </DialogContent>
    </Dialog>
  );
};
